/* 郭淮（字伯濟） */
import { SG, K, along, landing, spMul } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, S, D } from '../define.ts';

export default defineHero({
  id: 'guohuai', name: '郭淮', sub: '字伯濟', faction: 'wei', role: '戰士', weapon: 'spear',
  look: { beard: 'mustache', eye: 'narrow', fx: '#c9d8ff', c: { plume: '#3b5a99', cape: '#26335f' } },
  skills: [
    ['雍涼突刺', 'multi', '連刺兩段，每段 {70/110/150/190} 傷害，第二段擊退。', { x: { k: 'thrust2', dmg: [70, 110, 150, 190], r: 170 }, aim: D(170, 60), cd: 8, mp: [50, 60, 70, 80], el: null }],
    ['識破', 'shield', '架槍 0.75 秒，擋下所有傷害與控制；擋到控制技就暈眩施放者 {1/1.25/1.5/1.75} 秒，擋到傷害則反擊 {80/120/160/200}。', { x: { k: 'parry', dur: 0.75, stun: [1, 1.25, 1.5, 1.75], dmg: [80, 120, 160, 200] }, aim: S(0), cd: [18, 16, 14, 12], mp: 70, el: null }],
    ['拒蜀', 'passive', '對蜀國武將傷害 +{4/6/8/10}%；被蜀將技能打中時回復 {20/30/40/50} 生命。', { pv: {}, sig: 'antishu', amp: [4, 6, 8, 10], heal: [20, 30, 40, 50] }],
    ['羌騎奔襲', 'charge', '召來羌族騎兵從身後朝游標方向衝過 1200 距離，沿途敵人受到 {200/300/400} 傷害並擊飛 1 秒；郭淮騎上隊伍跟著衝出 600。', { x: { k: 'qiang', dmg: [200, 300, 400], len: 1200, w: 180 }, aim: D(1200, 90), cd: [100, 85, 70], mp: [150, 175, 200], el: 'earth', shout: '羌騎聽令，隨我破陣！' }],
  ],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    thrust2(u, a, X) {
      const sp = spMul(u), hit = (j: number) => this.cone(u, a.dir, X.r, 0.6).forEach((t) => this.damage(u, t, X.dmg * sp * (t.structure ? 0.5 : 1), { magic: true, spell: true, stagger: 0.3, knock: j ? 60 : 6 }));
      hit(0); this.later(0.25, () => { if (u.alive && u.st.stun <= 0) hit(1); });
    },
    parry(u, a, X) {
      u.parry = { until: this.t + X.dur, stun: X.stun, dmg: X.dmg * spMul(u), hit: false, stunned: false };
      u.st.root = Math.max(u.st.root, X.dur);
      this.fx({ k: 'spark', x: u.x, y: u.y - 50, col: '#c9d8ff' });
    },
    qiang(u, a, X) {
      const dir = a.dir, back = along(u.x, u.y, dir, -150);
      const p = this.shootProj(u, { dir, speed: 1000, range: X.len, rad: X.w / 2, shape: 'cavalry', el: 'earth', filter: (t: Unit) => !t.structure,
        onHit: (t: Unit) => { this.damage(u, t, X.dmg * spMul(u), { magic: true, spell: true, stagger: 0.4 }); this.applyCC(u, t, { knockup: 1 }); return false; } });
      p.x = back[0]; p.y = back[1]; p.dir = dir; p.onEnd = () => { p.over = true; };
      u.ride = p; u.slashing = true; u.act = null; u.anim = 'run'; u.animSeq++;
      this.say(`${u.name}：羌騎奔襲！`, 'info');
    },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    // 識破：架槍期間擋下所有傷害，第一次擋到就反擊
    hook('damage.before', 40, function (c) {
      const t = c.t, own0 = c.own;
      if (t.parry && t.parry.until > this.t && own0.team !== t.team) {
        if (!t.parry.hit && !t.parry.stunned && own0.alive && own0 !== t) { t.parry.hit = true; this.fx({ k: 'spark', x: t.x, y: t.y - 50, col: '#c9d8ff' }); c.base.call(this, t, own0, t.parry.dmg, { magic: true, spell: true, stagger: 0.3 }); }
        if (t.human || own0.human) this.dmgEvents.push({ x: t.x, y: t.y - 60, text: '識破', team: t.team, src: own0.id, tgt: t.id });
        return true;
      }
    }),
    // 拒蜀：打蜀將加傷；被蜀將的技能打中時回血（0.5 秒一次）
    hook('damage.before', 140, function (c) {
      const t = c.t, own0 = c.own, o = c.o;
      if (own0.sg && own0.sg.antishu && t.kind === 'hero' && t.spec.faction === 'shu') c.a *= 1 + own0.sg.antishu.v('amp') / 100;
      if (t.sg && t.sg.antishu && o && (o.spell || o.sk) && own0.kind === 'hero' && own0.spec.faction === 'shu' && !(t.antishuT > this.t)) { t.antishuT = this.t + 0.5; this.heal(t, t.sg.antishu.v('heal')); }
    }),
    // 識破：擋下控制並暈眩施放者
    hook('cc.block', 10, function (c) {
      const t = c.t, own = c.own, cc = c.cc;
      if (cc && t.parry && t.parry.until > this.t && own && own.team !== t.team) {
        if (SG.HARD_CC.some((k: string) => cc[k]) && !t.parry.stunned && own.alive && !own.structure) { t.parry.stunned = true; c.base.call(this, t, own, { stun: t.parry.stun }); this.fx({ k: 'thunder', x: own.x, y: own.y, col: '#c9d8ff' }); }
        return true;
      }
    }),
    // 羌騎奔襲：騎在騎兵上
    hook('tick', 90, function (u, dt) {
      if (u.ride) {
        const p = u.ride;
        if (p.over || p.dist > 600 || u.st.stun > 0) { u.ride = null; u.slashing = false; }
        else { const q = landing(u.x, u.y, p.x - p.dir[0] * 50, p.y - p.dir[1] * 50 * K); u.x = q[0]; u.y = q[1]; }
      }
    }),
    hook('revive', 10, function (u) { if (u.ride) u.slashing = false; u.ride = null; u.parry = null; }),
  ],
  // 電腦什麼時候放這招
  ai: {
    parry: ({ w, u, t, d, hero, hpR, foeHeroes }) => hero && d < 450 && ((t!.act && t!.act.kind === 'skill') || hpR < 0.5),
    qiang: ({ w, u, t, d, hero, hpR, foeHeroes }) => hero && d < 900,
  },
});
